import React, { useState, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight, FileText, Loader2, Printer, RefreshCw } from 'lucide-react';
import { api } from '../../services/api';
import { imprimerRecu } from '../../utils/imprimerRecu';

/**
 * Les reçus de caisse émis pour un élève ou une famille, avec leur
 * réimpression (lot 2, 04/11/2026). Celle d'une famille montre aussi les reçus
 * de ses enfants : inscription, réinscription, paiement d'un seul enfant.
 * Celle d'un candidat libre, ses frais d'examen (il n'a pas de dossier
 * financier où les retrouver).
 *
 * Un reçu est figé au jour de l'encaissement (RecuCaisseService) : le
 * réimprimer redonne le même numéro, le même montant, la même classe, avec la
 * mention DUPLICATA. C'est ce qui le distingue du relevé A4 d'un mois, qui
 * montre l'état du jour.
 *
 * Replié par défaut, comme l'historique voisin : chargé à la première
 * ouverture, il ne coûte rien à ceux qui ne l'ouvrent pas. `version` le
 * recharge quand un paiement vient d'être enregistré.
 */

const STATUTS = {
  emis: null,
  annule_partiel: { libelle: 'Annulé en partie', classe: 'border-amber-200 bg-amber-50 text-amber-800' },
  annule: { libelle: 'Annulé', classe: 'border-red-200 bg-red-50 text-red-700' }
};

// Ce que le reçu a encaissé (recus.origine) : un reçu d'inscription se
// retrouve d'un coup d'œil parmi les mensualités (décision du 04/11/2026).
const NATURES = {
  inscription: 'Inscription',
  reinscription: 'Réinscription',
  frais_eleve: 'Mensualités',
  famille_scolarite: 'Famille - scolarité',
  famille_annexes: 'Famille - cantine, bus',
  ponctuel: 'Ticket',
  examen: "Frais d'examen"
};

// Les mois payés, dans l'ordre de l'année (RecuCaisseService.listerRecus,
// séparés par « | ») : « Octobre, Novembre », ou « Octobre à Juin (9 mois) »
// pour des mois payés d'avance. La gestionnaire retrouve le reçu d'un mois
// d'un coup d'œil (décision de Daniel du 04/11/2026).
const moisDuRecu = (valeur) => {
  const mois = String(valeur || '').split('|').filter(Boolean);
  if (!mois.length) return '-';
  if (mois.length <= 3) return mois.join(', ');
  return `${mois[0]} à ${mois[mois.length - 1]} (${mois.length} mois)`;
};

const montant = (valeur) => `${Math.round(Number(valeur) || 0).toLocaleString('fr-FR')} FCFA`;

const dateHeure = (valeur) => {
  const date = valeur ? new Date(valeur) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ListeRecusEmis = ({ eleveId = null, familleId = null, candidatLibreId = null, anneeScolaireId = null, version = 0, onErreur }) => {
  const [ouvert, setOuvert] = useState(false);
  const [recus, setRecus] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [enImpression, setEnImpression] = useState(null);
  // Agent injoignable (reçu passé par le navigateur) ou imprimante hors ligne.
  const [note, setNote] = useState(null);

  const charger = useCallback(async () => {
    if (!eleveId && !familleId && !candidatLibreId) return;
    setChargement(true);
    const reponse = await api.recusCaisse.lister(candidatLibreId
      // Un candidat libre (frais d'examen) : ses reçus, toutes années.
      ? { candidat_libre_id: candidatLibreId }
      : {
        eleve_id: familleId ? null : eleveId,
        famille_id: familleId,
        annee_scolaire_id: anneeScolaireId
      });
    setChargement(false);
    if (reponse?.success) {
      setRecus(reponse.data || []);
      setErreur(null);
    } else {
      setErreur(reponse?.message || 'Les reçus n\'ont pas pu être lus');
    }
  }, [eleveId, familleId, candidatLibreId, anneeScolaireId]);

  // Rechargé après un paiement, s'il a déjà été ouvert.
  useEffect(() => {
    if (recus !== null) charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, charger]);

  const basculer = () => {
    const suivant = !ouvert;
    setOuvert(suivant);
    if (suivant && recus === null) charger();
  };

  const imprimer = async (recu) => {
    setEnImpression(recu.id);
    setNote(null);
    try {
      const { message } = await imprimerRecu(recu.id);
      if (message) setNote(message);
      setRecus((liste) => (liste || []).map((r) => (r.id === recu.id ? { ...r, impressions: (r.impressions || 0) + 1 } : r)));
    } catch (e) {
      if (onErreur) onErreur(e.message || "Le reçu n'a pas pu être imprimé");
    } finally {
      setEnImpression(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={basculer}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <FileText className="w-4 h-4 text-[#246990]" />
          Reçus de caisse
          {recus !== null && <span className="text-xs font-normal text-gray-500">({recus.length})</span>}
        </span>
        {ouvert ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {ouvert && (
        <div className="border-t border-gray-200 px-4 py-3">
          {chargement && recus === null && (
            <p className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des reçus…
            </p>
          )}

          {erreur && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-red-700">
              <span>{erreur}</span>
              <button type="button" onClick={charger} className="inline-flex items-center gap-1 font-semibold underline">
                <RefreshCw className="w-3 h-3" /> Réessayer
              </button>
            </div>
          )}

          {recus !== null && !erreur && recus.length === 0 && (
            <p className="text-xs text-gray-500">
              Aucun reçu émis. Chaque paiement enregistré depuis la mise en service des reçus de caisse en reçoit un.
            </p>
          )}

          {note && (
            <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{note}</p>
          )}

          {recus !== null && recus.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">N°</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Nature</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Mois</th>
                    {/* La famille voit aussi les reçus de ses enfants : pour qui. */}
                    {familleId && <th className="px-3 py-2 text-left font-medium text-gray-600">Bénéficiaire</th>}
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Montant</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Caissier</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">Reçu</th>
                  </tr>
                </thead>
                <tbody>
                  {recus.map((recu) => {
                    const statut = STATUTS[recu.statut];
                    return (
                      <tr key={recu.id} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <span className="font-mono text-xs font-semibold text-gray-800">{recu.numero}</span>
                          {statut && (
                            <span className={`ml-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statut.classe}`}>
                              {statut.libelle}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{dateHeure(recu.emis_le)}</td>
                        <td className="px-3 py-2 text-gray-700">{NATURES[recu.origine] || '-'}</td>
                        <td className="px-3 py-2 font-medium text-gray-800">{moisDuRecu(recu.mois)}</td>
                        {familleId && <td className="px-3 py-2 text-gray-700">{recu.beneficiaire || '-'}</td>}
                        <td className="px-3 py-2 text-right font-semibold text-[#246990]">{montant(recu.montant_total)}</td>
                        <td className="px-3 py-2 text-gray-700">{recu.caissier || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => imprimer(recu)}
                            disabled={enImpression === recu.id}
                            title={recu.impressions > 0 ? 'Déjà imprimé : sortira en DUPLICATA' : 'Imprimer le reçu'}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#246990] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#1f5c7d] disabled:opacity-50"
                          >
                            {enImpression === recu.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3" />}
                            {recu.impressions > 0 ? 'Réimprimer' : 'Imprimer'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ListeRecusEmis;
